import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { Checkbox } from '../../components/common/Checkbox';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  Product, 
  ProductType, 
  ProductFilters, 
  ProductStatus,
  ProductBulkOperation,
  BulkOperationResult
} from '../../types/pim';

export const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<ProductFilters>({
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_direction: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<'publish' | 'unpublish' | 'delete'>('publish');

  // Fetch products
  const { data: productsData, isLoading: loadingProducts, error: productsError } = useQuery({
    queryKey: ['pim-products', filters],
    queryFn: () => pimService.getProducts(filters),
    keepPreviousData: true,
    retry: false,
    onError: (error: any) => {
      if (error.response?.status === 404) {
        showNotification(t('pim.common.loadingError'), 'error');
      } else if (error.response?.status === 500) {
        showNotification(t('errors.serverError'), 'error');
      } else {
        showNotification(t('pim.common.loadingError'), 'error');
      }
    }
  });

  // Fetch product types for filter dropdown
  const { data: productTypesData } = useQuery({
    queryKey: ['pim-product-types-dropdown'],
    queryFn: () => pimService.getProductTypesForDropdown(),
    retry: false,
    onError: (error: any) => {
      showNotification(t('pim.common.loadingError'), 'warning');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteProduct(id),
    onSuccess: () => {
      showNotification(t('messages.success.deleted'), 'success');
      queryClient.invalidateQueries(['pim-products']);
      setShowDeleteModal(false);
      setProductToDelete(null);
    },
    onError: (error: any) => {
      showNotification(error.message || t('errors.general'), 'error');
    }
  });

  // Bulk operation mutation
  const bulkMutation = useMutation({
    mutationFn: (operation: ProductBulkOperation) => pimService.productBulkOperation(operation),
    onSuccess: (data) => {
      const result = data.data;
      if (result.failed > 0) {
        showNotification(
          `${t('pim.common.processing')} ${result.processed}, ${t('errors.general')}: ${result.failed}`,
          'warning'
        );
      } else {
        showNotification(t('messages.success.updated'), 'success');
      }
      queryClient.invalidateQueries(['pim-products']);
      setSelectedProducts([]);
      setShowBulkModal(false);
    },
    onError: (error: any) => {
      showNotification(error.message || t('errors.general'), 'error');
    }
  });

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof ProductFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_direction: prev.sort_by === sortBy && prev.sort_direction === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const renderSortableHeader = (label: string, sortKey: string) => {
    const isActive = filters.sort_by === sortKey;
    const isAsc = filters.sort_direction === 'asc';

    return (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {isActive && (
            isAsc ?
              <ChevronUpIcon className="h-4 w-4" /> :
              <ChevronDownIcon className="h-4 w-4" />
          )}
        </div>
      </th>
    );
  };

  const handleSelectProduct = (productId: number) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (!productsData?.data) return;
    
    const allIds = productsData.data.map(p => p.id);
    setSelectedProducts(prev => 
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleDeleteProduct = (product: Product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleBulkOperation = () => {
    const action = bulkAction === 'publish' ? 'publish' : 
                   bulkAction === 'unpublish' ? 'unpublish' : 'delete';
    
    bulkMutation.mutate({
      action,
      productIds: selectedProducts
    });
  };

  const getStatusBadge = (status: ProductStatus) => {
    switch (status) {
      case 'published':
        return <Badge variant="success" icon={CheckCircleIcon}>{t('pim.products.status.published')}</Badge>;
      case 'draft':
        return <Badge variant="warning" icon={ClockIcon}>{t('pim.products.status.draft')}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_direction: 'desc'
    });
  };

  if (productsError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('pim.common.loadingError')}: {(productsError as any).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.products.title')}</h1>
          <p className="text-gray-600">{t('pim.products.subtitle')}</p>
        </div>
        {permissions.canWriteProducts && (
          <Link to="/pim/products/new">
            <Button icon={PlusIcon}>
              {t('pim.products.addProduct')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('pim.products.searchPlaceholder')}
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                icon={MagnifyingGlassIcon}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                icon={FunnelIcon}
                onClick={() => setShowFilters(!showFilters)}
              >
                {t('pim.products.filters')}
              </Button>
              {(filters.search || filters.status?.length || filters.product_type_id?.length) && (
                <Button
                  variant="outline"
                  icon={XMarkIcon}
                  onClick={clearFilters}
                >
                  {t('pim.products.clearFilters')}
                </Button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <Select
                label={t('pim.products.statusLabel')}
                value={filters.status?.[0] || ''}
                onChange={(e) => handleFilterChange('status', e.target.value ? [e.target.value] : undefined)}
                options={[
                  { value: '', label: t('pim.products.allStatuses') },
                  { value: 'draft', label: t('pim.products.status.draft') },
                  { value: 'published', label: t('pim.products.status.published') }
                ]}
              />
              <Select
                label={t('pim.products.productType')}
                value={filters.product_type_id?.[0]?.toString() || ''}
                onChange={(e) => handleFilterChange('product_type_id', e.target.value ? [parseInt(e.target.value)] : undefined)}
                options={[
                  { value: '', label: t('pim.products.allTypes') },
                  ...(productTypesData?.data || []).map(type => ({
                    value: type.id.toString(),
                    label: type.name
                  }))
                ]}
              />
              <Select
                label={t('pim.products.sortBy')}
                value={filters.sort_by || 'created_at'}
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                options={[
                  { value: 'created_at', label: t('pim.products.createdAt') },
                  { value: 'updated_at', label: t('pim.products.updatedAt') },
                  { value: 'name', label: t('pim.products.name') },
                  { value: 'sku', label: t('pim.products.sku') }
                ]}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!filters.has_media}
                  onChange={(e) => handleFilterChange('has_media', e.target.checked)}
                  className="h-4 w-4"
                />
                {t('navigation.pim.media')}
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedProducts.length > 0 && permissions.canWriteProducts && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {t('pim.products.selected', { count: selectedProducts.length })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkAction('publish');
                    setShowBulkModal(true);
                  }}
                >
                  {t('pim.products.publish')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkAction('unpublish');
                    setShowBulkModal(true);
                  }}
                >
                  {t('pim.products.unpublish')}
                </Button>
                {permissions.canDeleteProducts && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setBulkAction('delete');
                      setShowBulkModal(true);
                    }}
                  >
                    {t('common.delete')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Table */}
      <Card>
        <CardContent>
          {loadingProducts ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">{t('pim.products.loading')}</p>
            </div>
          ) : !productsData?.data?.length ? (
            <div className="text-center py-12">
              <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.products.noProducts')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('pim.products.noProductsDescription')}</p>
              {permissions.canWriteProducts && (
                <div className="mt-6">
                  <Link to="/pim/products/new">
                    <Button icon={PlusIcon}>
                      {t('pim.products.addProduct')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {permissions.canWriteProducts && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <Checkbox
                          checked={selectedProducts.length === productsData.data.length}
                          indeterminate={selectedProducts.length > 0 && selectedProducts.length < productsData.data.length}
                          onChange={handleSelectAll}
                        />
                      </th>
                    )}
                    {renderSortableHeader(t('pim.products.sku'), 'sku')}
                    {renderSortableHeader(t('pim.products.name'), 'name')}
                    {renderSortableHeader(t('pim.products.statusLabel'), 'status')}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.products.type')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('navigation.pim.media')}
                    </th>
                    {renderSortableHeader(t('pim.products.created'), 'created_at')}
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions.title')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {productsData.data.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      {permissions.canWriteProducts && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Checkbox
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                          />
                        </td>
                      )}
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => navigate(`/pim/products/${product.id}`)}
                      >
                        <div className="text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors">
                          {product.sku}
                        </div>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => navigate(`/pim/products/${product.id}`)}
                      >
                        <div className="text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors">
                          {product.name}
                        </div>
                        {product.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {product.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(product.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {product.productType?.name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <Link to={`/pim/products/${product.id}#media`} className="text-primary-600 hover:underline">
                          {(product as any).mediaCount ?? 0}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.created_at ? new Date(product.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex justify-center gap-2">
                          {/* Single action: Edit (full edit view) */}
                          {permissions.canWriteProducts && (
                            <Link to={`/pim/products/${product.id}`}>
                              <Button variant="ghost" size="sm" icon={PencilIcon}>
                                {t('common.edit')}
                              </Button>
                            </Link>
                          )}
                          {permissions.canDeleteProducts && (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={TrashIcon}
                              onClick={() => handleDeleteProduct(product)}
                            >
                              {t('common.delete')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {productsData?.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={productsData.pagination.page}
                totalPages={productsData.pagination.totalPages}
                totalItems={productsData.pagination.total}
                itemsPerPage={productsData.pagination.limit}
                onPageChange={(page) => handleFilterChange('page', page)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('pim.products.deleteProduct')}
      >
        <div className="space-y-4">
          <p>
            {t('pim.products.confirmDelete', { name: productToDelete?.name })}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => productToDelete && deleteMutation.mutate(productToDelete.id)}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? t('pim.common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={t('pim.products.bulkActions')}
      >
        <div className="space-y-4">
          <p>
            {bulkAction === 'publish' && t('pim.products.confirmBulkPublish', { count: selectedProducts.length })}
            {bulkAction === 'unpublish' && t('pim.products.confirmBulkUnpublish', { count: selectedProducts.length })}
            {bulkAction === 'delete' && t('pim.products.confirmBulkDelete', { count: selectedProducts.length })}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant={bulkAction === 'delete' ? 'danger' : 'primary'}
              onClick={handleBulkOperation}
              disabled={bulkMutation.isLoading}
            >
              {bulkMutation.isLoading ? t('pim.common.processing') :
                (bulkAction === 'publish' ? t('pim.products.publish') :
                 bulkAction === 'unpublish' ? t('pim.products.unpublish') :
                 t('common.delete'))}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
